import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { Trash2, Plus, Star, Film, ImageIcon, ArrowLeft, Pencil, Check, X } from "lucide-react";
import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { GeneroLabelMap } from "../../lib/GeneroLabel";
import { GeneroEnum } from "../../models/enums/GeneroEnum";
import { Album } from "../../models/Album";
import { Filme } from "../../models/Filme";
import { toast, Toaster } from "../../components/ui/sooner";
import { createAlbum, deleteAlbum, listAlbums, listImagesByAlbum, updateAlbum, uploadPhoto } from "../../services/AlbumService";
import { createMovie, deleteMovie, listMovies, updateMovie } from "../../services/FilmeService";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { ScrollArea } from "../../components/ui/scrollarea";
import { FotoDTO } from "../../models/DTOs/FotoDTO";
import { deleteFoto } from "../../services/FotoService";

const Admin = () => {
  const generos = Object.keys(GeneroLabelMap).map(Number).filter((v) => !isNaN(v)) as GeneroEnum[];

  // =====================
  // Estados principais
  // =====================
  const [albums, setAlbums] = useState<Album[]>([]);
  const [filmes, setFilmes] = useState<Filme[]>([]);

  // Formulário criar álbum
  const [albumNome, setAlbumNome] = useState("");
  const [albumTrancado, setAlbumTrancado] = useState(false);
  const [albumTrancadoAte, setAlbumTrancadoAte] = useState("");

  // Formulário criar filme
  const [filmeNome, setFilmeNome] = useState("");
  const [filmeGenero, setFilmeGenero] = useState<GeneroEnum>(GeneroEnum.Drama);
  const [filmeNota, setFilmeNota] = useState(0);

  // Edição de álbum
  const [editingAlbum, setEditingAlbum] = useState<Album | null>(null);
  const [editAlbumName, setEditAlbumName] = useState("");
  const [editAlbumTrancadoAte, setEditAlbumTrancadoAte] = useState("");
  const [albumPhotos, setAlbumPhotos] = useState<FotoDTO[]>([]);
  const [editAlbumCover, setEditAlbumCover] = useState("");
  const [newPhotoFiles, setNewPhotoFiles] = useState<File[]>([]);

  // Edição de filme
  const [editingMovie, setEditingMovie] = useState<Filme | null>(null);
  const [editMovieTitle, setEditMovieTitle] = useState("");
  const [editMovieGenero, setEditMovieGenero] = useState<GeneroEnum>(GeneroEnum.Drama);
  const [editMovieNota, setEditMovieNota] = useState(0);

  // =====================
  // Funções de álbum
  // =====================
  const handleEditAlbum = async (album: Album) => {
    setEditingAlbum(album);
    setEditAlbumName(album.nome);
    setEditAlbumTrancadoAte(
      album.trancadaAte ? new Date(album.trancadaAte).toISOString().split("T")[0] : ""
    );

    const fotos = await listImagesByAlbum(album.id) || []; // se for undefined, usa []

    setAlbumPhotos(fotos);
  };

  const handleAddPhotos = async () => {
    if (newPhotoFiles.length === 0 || !editingAlbum) return;

    const formData = new FormData();
    formData.append("albumId", editingAlbum.id);

    newPhotoFiles.forEach(file => {
      formData.append("arquivos", file);
    });

    try {
      await uploadPhoto(formData);

      setAlbumPhotos(await listImagesByAlbum(editingAlbum.id) ?? []);
      setNewPhotoFiles([]);

      toast.success("Fotos adicionadas com sucesso!");
    } catch (error) {
      console.error(error);
      toast.error("Erro ao enviar fotos");
    }
  };


  const handleSetAsCover = (arquivoNome: string) => {
    setEditAlbumCover(arquivoNome);

    setAlbumPhotos((prev) =>
      prev.map((p) => ({
        ...p,
        capa: p.arquivoNome === arquivoNome,
      }))
    );
  };

  const deletePhoto = {
    mutate: async (fotoId: string) => {
      try {
        await deleteFoto(fotoId);

        setAlbumPhotos((prev) =>
          prev.filter((f) => f.id !== fotoId)
        );
      } catch (error) {
        console.error("Erro ao deletar foto:", error);
      }
    },
  };

  const handleEditAlbumSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingAlbum) return;
    if (!editAlbumName.trim()) return toast.error("O nome do álbum é obrigatório");

    try {
      const trancadoAteIso = editAlbumTrancadoAte ? new Date(editAlbumTrancadoAte).toISOString() : undefined;

      await updateAlbum(editingAlbum.id, 
        editAlbumName,
        editAlbumCover,
        trancadoAteIso
      );

      setAlbums(await listAlbums() ?? []);
      toast.success("Álbum atualizado com sucesso!");
      setEditingAlbum(null);
    } catch (error: any) {
        console.error(error);

        const message =
          error?.response?.data ||
          error?.response?.data?.message ||
          "Erro ao atualizar álbum";

        toast.error(message);
      }
  };

  const handleCreateAlbum = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!albumNome.trim()) return toast.error("O nome do álbum é obrigatório");

    try {
      const trancadoAteIso = albumTrancado && albumTrancadoAte ? new Date(albumTrancadoAte).toISOString() : null;
      await createAlbum(albumNome, trancadoAteIso);
      setAlbums(await listAlbums() ?? []);
      setAlbumNome("");
      setAlbumTrancado(false);
      setAlbumTrancadoAte("");
      toast.success("Álbum criado com sucesso!");
    } catch (error) {
      console.error(error);
      toast.error("Erro ao criar álbum");
    }
  };

  const handleDeleteAlbum = async (id: string) => {
    try {
      await deleteAlbum(id);

      setAlbums(prev => prev.filter(a => a.id !== id));
      toast.success("Álbum deletado com sucesso!");
    } catch (error: any) {
      console.error(error);

      const message =
        error?.response?.data ||
        error?.response?.data?.message ||
        "Erro ao deletar álbum";

      toast.error(message);
    }
  };

  // =====================
  // Funções de filme
  // =====================
  const handleEditMovie = (filme: Filme) => {
    setEditingMovie(filme);
    setEditMovieTitle(filme.nome);
    setEditMovieGenero(filme.genero);
    setEditMovieNota(filme.nota);
  };

  const handleEditMovieSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingMovie) return;
    if (!editMovieTitle.trim()) return toast.error("O nome do filme é obrigatório");

    try {
      await updateMovie(editingMovie.id, editMovieTitle, editMovieGenero, editMovieNota );
      setFilmes((await listMovies()).items ?? []);
      toast.success("Filme atualizado com sucesso!");
      setEditingMovie(null);
    } catch (error) {
      console.error(error);
      toast.error("Erro ao atualizar filme");
    }
  };

  const handleCreateFilme = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!filmeNome.trim()) return toast.error("O nome do filme é obrigatório");

    try {
      await createMovie(filmeNome, filmeGenero, filmeNota);
      setFilmes((await listMovies()).items ?? []);
      setFilmeNome("");
      setFilmeGenero(GeneroEnum.Acao);
      setFilmeNota(0);
      toast.success("Filme criado com sucesso!");
    } catch (error) {
      console.error(error);
      toast.error("Erro ao criar filme");
    }
  };

  const handleDeleteFilme = async (id: string) => {
    try {
      await deleteMovie(id);
      setFilmes(prev => prev.filter(f => f.id !== id));
      toast.success("Filme deletado com sucesso!");
    } catch (error) {
      console.error(error);
      toast.error("Erro ao deletar filme");
    }
  };

  // =====================
  // Load inicial
  // =====================
  useEffect(() => {
    const fetchData = async () => {
      try {
        setAlbums(await listAlbums() ?? []);
        setFilmes((await listMovies()).items ?? []);
      } catch (error) {
        console.error(error);
        toast.error("Erro ao carregar dados");
      }
    };
    fetchData();
  }, []);

  const renderStars = (rating: number, setRating: (n: number) => void) => (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map(star => (
        <button key={star} type="button" onClick={() => setRating(star)}>
          <Star className={`w-6 h-6 ${star <= rating ? "fill-yellow-400 text-yellow-400" : "text-muted-foreground"}`} />
        </button>
      ))}
    </div>
  );

  // =====================
  // JSX
  // =====================
  return (
    <div className="min-h-screen bg-background p-4 md:p-8">
      <Toaster />
      <div className="max-w-4xl mx-auto">
        {/* HEADER */}
        <div className="flex items-center gap-4 mb-8">
          <Link to="/">
            <Button variant="ghost" size="icon"><ArrowLeft className="w-5 h-5" /></Button>
          </Link>
          <h1 className="text-3xl font-serif text-primary">Painel Admin</h1>
        </div>

        <Tabs defaultValue="albums">
          <TabsList className="grid grid-cols-2 mb-6">
            <TabsTrigger value="albums"><ImageIcon className="w-4 h-4 mr-2" />Álbuns</TabsTrigger>
            <TabsTrigger value="movies"><Film className="w-4 h-4 mr-2" />Filmes</TabsTrigger>
          </TabsList>

          {/* ÁLBUNS */}
          <TabsContent value="albums" className="space-y-6">
            <Card>
              <CardHeader><CardTitle className="flex items-center gap-2"><Plus className="w-5 h-5" />Novo Álbum</CardTitle></CardHeader>
              <CardContent>
                <form onSubmit={handleCreateAlbum} className="space-y-4">
                  <div><Label>Nome *</Label><Input value={albumNome} onChange={e => setAlbumNome(e.target.value)} /></div>
                  <div className="flex items-center gap-2">
                    <Switch checked={albumTrancado} onCheckedChange={setAlbumTrancado} />
                    <Label>Álbum trancado</Label>
                  </div>
                  {albumTrancado && (
                    <Input type="date" value={albumTrancadoAte} onChange={e => setAlbumTrancadoAte(e.target.value)} />
                  )}
                  <Button type="submit" className="w-full">Criar Álbum</Button>
                </form>
              </CardContent>
            </Card>

            <Card>
              <CardHeader><CardTitle>Álbuns ({albums.length})</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                {albums.map(album => (
                  <div key={album.id} className="flex justify-between items-center p-3 bg-muted/50 rounded-lg">
                    <div>
                      <p className="font-medium">{album.nome}</p>
                      {album.trancadaAte && <p className="text-sm text-muted-foreground">🔒 Até {album.trancadaAte}</p>}
                    </div>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="icon" onClick={() => handleEditAlbum(album)}><Pencil className="w-4 h-4" /></Button>
                      <Button variant="ghost" size="icon" onClick={() => handleDeleteAlbum(album.id)}><Trash2 className="w-4 h-4 text-destructive" /></Button>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          </TabsContent>

          {/* FILMES */}
          <TabsContent value="movies" className="space-y-6">
            <Card>
              <CardHeader><CardTitle className="flex items-center gap-2"><Plus className="w-5 h-5" />Novo Filme</CardTitle></CardHeader>
              <CardContent>
                <form onSubmit={handleCreateFilme} className="space-y-4">
                  <Input placeholder="Nome do filme" value={filmeNome} onChange={e => setFilmeNome(e.target.value)} />
                  <select className="w-full border rounded-md p-2" value={filmeGenero} onChange={e => setFilmeGenero(Number(e.target.value) as GeneroEnum)}>
                    {generos.map(g => <option key={g} value={g}>{GeneroLabelMap[g]}</option>)}
                  </select>
                  {renderStars(filmeNota, setFilmeNota)}
                  <Button type="submit" className="w-full">Adicionar Filme</Button>
                </form>
              </CardContent>
            </Card>

            <Card>
              <CardHeader><CardTitle>Filmes ({filmes.length})</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                {filmes.map(filme => (
                  <div key={filme.id} className="flex justify-between items-center p-3 bg-muted/50 rounded-lg">
                    <div>
                      <p className="font-medium">{filme.nome}</p>
                      <p className="text-sm text-muted-foreground">{GeneroLabelMap[filme.genero]} • ⭐ {filme.nota}</p>
                    </div>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="icon" onClick={() => handleEditMovie(filme)}><Pencil className="w-4 h-4" /></Button>
                      <Button variant="ghost" size="icon" onClick={() => handleDeleteFilme(filme.id)}><Trash2 className="w-4 h-4 text-destructive" /></Button>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Modal de edição de álbum */}
      <Dialog open={!!editingAlbum} onOpenChange={(open) => !open && setEditingAlbum(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle>Editar Álbum</DialogTitle>
          </DialogHeader>

          <ScrollArea className="max-h-[70vh] pr-4">
            <form onSubmit={handleEditAlbumSubmit} className="space-y-4">
              {/* Nome */}
              <div className="space-y-2">
                <Label>Nome do Álbum *</Label>
                <Input value={editAlbumName} onChange={(e) => setEditAlbumName(e.target.value)} />
              </div>

              {/* Trancado */}
              <div className="flex items-center gap-2">
                <Switch
                  checked={!!editAlbumTrancadoAte}
                  onCheckedChange={(checked) =>
                    !checked
                      ? setEditAlbumTrancadoAte("")
                      : setEditAlbumTrancadoAte(new Date().toISOString().split("T")[0])
                  }
                />
                <Label>Álbum trancado</Label>
              </div>

              {editAlbumTrancadoAte && (
                <Input
                  type="date"
                  value={editAlbumTrancadoAte}
                  onChange={(e) => setEditAlbumTrancadoAte(e.target.value)}
                />
              )}

              {/* Fotos */}
              <div className="space-y-4 pt-4 border-t">
                <Label className="text-base font-semibold">Fotos do Álbum</Label>

                {/* Upload */}
                <div className="flex gap-2">
                  <Input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={(e) => {
                      const files = e.target.files;
                      setNewPhotoFiles(files ? Array.from(files) : []);
                    }}
                    className="flex-1"
                  />
                  <Button
                    type="button"
                    onClick={handleAddPhotos}
                    size="icon"
                    disabled={newPhotoFiles.length === 0}
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>

                {/* Lista de fotos */}
                {albumPhotos?.length === 0 ? (
                  <p className="text-muted-foreground text-center py-4 text-sm">
                    Nenhuma foto no álbum ainda
                  </p>
                ) : (
                  <div className="grid grid-cols-3 gap-3">
                    {albumPhotos.map((photo) => {
                      const isCover = photo.capa;
                      return (
                        <div
                          key={photo.id}
                          className={`relative group aspect-square rounded-lg overflow-hidden border-2 ${isCover ? "border-primary" : "border-transparent"
                            }`}
                        >
                          {photo.video ? (
                            <video
                              src={photo.url}
                              muted
                              preload="metadata"
                              playsInline
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <img
                              src={photo.url}
                              alt="Foto do álbum"
                              className="w-full h-full object-cover"
                            />
                          )}
                          {isCover && (
                            <div className="absolute top-1 left-1 bg-primary text-primary-foreground text-xs px-2 py-0.5 rounded">
                              Capa
                            </div>
                          )}
                          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                            {!isCover && (
                              <Button
                                type="button"
                                size="icon"
                                variant="secondary"
                                className="h-8 w-8"
                                onClick={() => handleSetAsCover(photo.arquivoNome)}
                                title="Definir como capa"
                              >
                                <Check className="w-4 h-4" />
                              </Button>
                            )}
                            <Button
                              type="button"
                              size="icon"
                              variant="destructive"
                              className="h-8 w-8"
                              onClick={() => deletePhoto.mutate(photo.id)}
                              title="Excluir foto"
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Ações */}
              <div className="flex gap-2 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  className="flex-1"
                  onClick={() => setEditingAlbum(null)}
                >
                  Cancelar
                </Button>
                <Button type="submit" className="flex-1">
                  Salvar
                </Button>
              </div>
            </form>
          </ScrollArea>
        </DialogContent>
      </Dialog>

      {/* Modal de edição de filme */}
      <Dialog open={!!editingMovie} onOpenChange={(open) => !open && setEditingMovie(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Editar Filme</DialogTitle></DialogHeader>
          <form onSubmit={handleEditMovieSubmit} className="space-y-4">
            <Input value={editMovieTitle} onChange={e => setEditMovieTitle(e.target.value)} placeholder="Nome do filme" />
            <select className="w-full border rounded-md p-2" value={editMovieGenero} onChange={e => setEditMovieGenero(Number(e.target.value) as GeneroEnum)}>
              {generos.map(g => <option key={g} value={g}>{GeneroLabelMap[g]}</option>)}
            </select>
            {renderStars(editMovieNota, setEditMovieNota)}
            <div className="flex gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setEditingMovie(null)}>Cancelar</Button>
              <Button type="submit">Salvar</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Admin;
